export interface FetchResourceParams {
  [key: string]:
    | string
    | number
    | boolean
    | Array<string | number | boolean>
    | undefined
}

export interface FetchResourceOptions {
  signal?: AbortSignal
  skipAuthRetry?: boolean
}

const DEFAULT_HEADERS: Record<string, string> = {
  Accept: 'application/json',
}

export const API_BASE = import.meta.env.VITE_ENGINE_ADMIN_API.replace(/\/$/, '')

const getHeaders = (): Record<string, string> => {
  const headers = { ...DEFAULT_HEADERS }
  return headers
}

const buildQueryString = (params: FetchResourceParams): string => {
  const searchParams = new URLSearchParams()

  Object.entries(params)
    .filter(
      (
        entry,
      ): entry is [string, Exclude<FetchResourceParams[string], undefined>] =>
        entry[1] !== undefined,
    )
    .forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(val => searchParams.append(key, String(val)))
        return
      }

      searchParams.set(key, String(value))
    })

  const queryString = searchParams.toString()

  return queryString.length > 0 ? `?${queryString}` : ''
}

export async function adminFetch<T>(
  resource: string,
  req?:
    | { method?: 'GET'; params?: FetchResourceParams; signal?: AbortSignal }
    | {
        json?: any
        method: 'POST' | 'PUT' | 'DELETE'
        params?: FetchResourceParams
        signal?: AbortSignal
      },
): Promise<T> {
  const url = `${API_BASE}/${resource}${buildQueryString(req?.params || {})}`
  const hasJsonBody = req && 'json' in req
  const response = await fetch(url, {
    headers: {
      ...getHeaders(),
      ...(hasJsonBody && { 'Content-Type': 'application/json' }),
    },
    credentials: 'include', // Include cookies for session authentication
    signal: req?.signal,
    body: hasJsonBody ? JSON.stringify(req.json) : undefined,
    method: req?.method || 'GET',
  })

  if (!response.ok) {
    let errorBody = ''
    let errorCode = ''

    try {
      const errorJson = await response.json()
      errorBody = errorJson.error || JSON.stringify(errorJson)
      errorCode = errorJson.code || ''
    } catch {
      errorBody = await response.text()
    }

    const errorMessage = `Failed to load ${resource}: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`

    // Attach status and code for better error handling
    const error = new Error(errorMessage)
    ;(error as any).status = response.status
    ;(error as any).code = errorCode

    throw error
  }

  return response.json() as Promise<T>
}

export { buildQueryString }
